"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { MapPin, Building2, Star, Code as Code2, Target, FileText, Globe as Globe2, Link2, Smartphone, Search, ChartBar as BarChart3, Zap, ArrowRight, CircleCheck as CheckCircle2, Calendar } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
interface ServiceItem {
  id: number;
  title: string;
  category: string;
  description: string;
  keyDeliverable: string;
  iconName: string;
}
const servicesData: ServiceItem[] = [{
  id: 1,
  title: "Google Business Profile Optimization",
  category: "Map Pack & Local",
  description: "Complete GBP configuration, category refinement, geotagged photo management, and ongoing strategy to dominate local map 3-packs.",
  keyDeliverable: "100% verified profile audit & optimization",
  iconName: "MapPin"
}, {
  id: 2,
  title: "Local Citation Building & Audit",
  category: "Authority & NAP",
  description: "Cleaning up inconsistent NAP (Name, Address, Phone) data and placing your business on high-trust UK and global directory networks.",
  keyDeliverable: "Clean NAP audit & high-tier directory links",
  iconName: "Building2"
}, {
  id: 3,
  title: "Review Strategy & Reputation Management",
  category: "Conversion & Trust",
  description: "Ethical customer feedback systems and direct consultation to safely accelerate 5-star Google reviews and convert local searchers.",
  keyDeliverable: "Automated review request framework",
  iconName: "Star"
}, {
  id: 4,
  title: "Local Schema Markup Implementation",
  category: "Technical SEO",
  description: "Custom JSON-LD structured data for LocalBusiness, geo-coordinates, operating hours, and multi-location service area coverage.",
  keyDeliverable: "Custom schema deployment & rich results testing",
  iconName: "Code2"
}, {
  id: 5,
  title: "Geo-Targeted Content Strategy",
  category: "Content Engine",
  description: "Creating high-converting localized service pages and regional topic clusters engineered to capture high-intent local queries.",
  keyDeliverable: "Targeted location page architecture",
  iconName: "Target"
}, {
  id: 6,
  title: "On-Page Local SEO Refinement",
  category: "Search Relevancy",
  description: "Precision optimization of meta titles, localized H1-H3 headers, internal linking, and transactional local search intent elements.",
  keyDeliverable: "On-page optimization across key service pages",
  iconName: "FileText"
}, {
  id: 7,
  title: "Multi-Location SEO Architecture",
  category: "Scalable Growth",
  description: "Tailored structures for businesses operating in multiple UK cities or regions, preventing keyword cannibalization across branches.",
  keyDeliverable: "Scalable multi-branch hierarchy & map strategy",
  iconName: "Globe2"
}, {
  id: 8,
  title: "Local Authority & Link Outreach",
  category: "Link Building",
  description: "Securing contextual backlinks from reputable regional press, industry partners, local business associations, and editorial sources.",
  keyDeliverable: "High-authority editorial local link placements",
  iconName: "Link2"
}, {
  id: 9,
  title: "Mobile & Voice Search Optimization",
  category: "UX & Speed",
  description: "Optimizing for conversational 'near me' queries, Core Web Vitals, and lightning-fast mobile performance for instant phone call leads.",
  keyDeliverable: "Mobile speed audit & voice intent mapping",
  iconName: "Smartphone"
}, {
  id: 10,
  title: "Hyper-Local Keyword Research",
  category: "Search Strategy",
  description: "Uncovering neighborhood-level search trends, commercial intent keywords, and lower-competition geo-targeted search phrases.",
  keyDeliverable: "Comprehensive local keyword matrix",
  iconName: "Search"
}, {
  id: 11,
  title: "Local Competitor Intelligence",
  category: "Market Analysis",
  description: "Deep audit of top-ranking local competitors to reverse engineer their citation sources, link velocity, content structure, and GBP tactics.",
  keyDeliverable: "Gap analysis report & action roadmap",
  iconName: "BarChart3"
}, {
  id: 12,
  title: "Local Search Conversion Optimization",
  category: "Lead Generation",
  description: "Turning map views and organic website visits into direction requests, phone calls, and direct consultation bookings.",
  keyDeliverable: "Call-to-action optimization & conversion tracking",
  iconName: "Zap"
}];
export default function Service() {
  const shouldReduceMotion = useReducedMotion();
  const getIcon = (iconName: string) => {
    switch (iconName) {
      case "MapPin":
        return <MapPin className="size-5 shrink-0" />;
      case "Building2":
        return <Building2 className="size-5 shrink-0" />;
      case "Star":
        return <Star className="size-5 shrink-0" />;
      case "Code2":
        return <Code2 className="size-5 shrink-0" />;
      case "Target":
        return <Target className="size-5 shrink-0" />;
      case "FileText":
        return <FileText className="size-5 shrink-0" />;
      case "Globe2":
        return <Globe2 className="size-5 shrink-0" />;
      case "Link2":
        return <Link2 className="size-5 shrink-0" />;
      case "Smartphone":
        return <Smartphone className="size-5 shrink-0" />;
      case "Search":
        return <Search className="size-5 shrink-0" />;
      case "BarChart3":
        return <BarChart3 className="size-5 shrink-0" />;
      case "Zap":
        return <Zap className="size-5 shrink-0" />;
      default:
        return <MapPin className="size-5 shrink-0" />;
    }
  };
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.05
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
        ease: "easeOut"
      }
    }
  };
  return <section id="servicegridconsultancy" data-nav="light" className="bg-background text-foreground relative py-16 md:py-24">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-3xl text-center">
          <Badge variant="secondary" className="mb-4 rounded-full px-3 py-1 font-medium text-xs text-primary">
            Hands-On SEO Expertise
          </Badge>

          <h2 className="text-2xl font-bold tracking-tight text-foreground sm:text-3xl lg:text-4xl font-default">
            Specialized Local SEO Solutions
          </h2>

          <p className="mt-4 text-base text-muted-foreground sm:text-lg">
            When you hire me, you work directly with me — no account managers, junior staff, or agency fluff. Here are the core local SEO levers I deploy to drive sustainable search revenue for your business.
          </p>
        </div>

        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        margin: "-50px"
      }} className="mt-12 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {servicesData.map((service, i) => <motion.div key={service.id} variants={itemVariants} data-index={i} className="h-full">
              <Card className="flex h-full flex-col overflow-hidden border border-border bg-card shadow-sm transition-[border-color,box-shadow] duration-200 hover:border-primary/40 hover:shadow-md">
                <CardHeader className="bg-primary text-primary-foreground flex flex-col justify-between gap-3 p-5">
                  <div className="flex items-center justify-between gap-2">
                    <span className="inline-flex size-9 items-center justify-center rounded-lg bg-primary-foreground/15 text-primary-foreground backdrop-blur-xs">
                      {getIcon(service.iconName)}
                    </span>
                    <Badge variant="outline" className="border-primary-foreground/30 bg-primary-foreground/10 text-primary-foreground text-[11.5px]">
                      {service.category}
                    </Badge>
                  </div>
                  <CardTitle className="text-lg font-semibold leading-tight text-primary-foreground font-default">
                    {service.title}
                  </CardTitle>
                </CardHeader>

                <CardContent className="flex flex-1 flex-col justify-between p-5 text-card-foreground">
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {service.description}
                  </p>

                  <div className="mt-6 border-t border-border/60 pt-4">
                    <div className="flex items-start gap-2 text-xs text-foreground font-medium">
                      <CheckCircle2 className="mt-0.5 size-4 shrink-0 text-primary" />
                      <span>{service.keyDeliverable}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>)}
        </motion.div>

        <div className="mt-16 rounded-2xl bg-muted/60 p-6 border border-border sm:p-8 md:p-10">
          <div className="flex flex-col items-center justify-between gap-6 text-center lg:flex-row lg:text-left">
            <div className="max-w-2xl">
              <h3 className="text-xl font-semibold text-foreground sm:text-2xl font-default">
                Need a tailored strategy for your specific locations?
              </h3>
              <p className="mt-2 text-sm text-muted-foreground sm:text-base">
                I do not sell rigid one-size-fits-all packages. I analyze your business, industry competitors, and target geographic areas to create an actionable, high-ROI campaign.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row items-center gap-3 shrink-0">
              <Button asChild size="lg" className="rounded-md font-medium text-sm">
                <Link to="/ContactPage" className="inline-flex items-center gap-2">
                  <SafeHydrate><Calendar className="size-4" /></SafeHydrate>
                  <span>Book Free Consultation</span>
                </Link>
              </Button>

              <Button asChild variant="outline" size="lg" className="rounded-md font-medium text-sm">
                <Link to="/CaseStudiesPage" className="inline-flex items-center gap-2">
                  <span>Explore Case Studies</span>
                  <ArrowRight className="size-4" />
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>;
}